import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  LayoutDashboard, Film, Tv, Clapperboard, Tag,
  CreditCard, BarChart3, Star, Settings, ArrowLeft, Bell,
  ChevronLeft, ChevronRight, Send, Sun, Moon, Ticket, Filter, Joystick, Image, RefreshCw, Smile, Megaphone, Video, Baby, UserCheck, Trash2, Flag, Mail, Shield, Bot, Menu, Smartphone
} from 'lucide-react';
import {
  DashboardTab, ContentsTab, CategoriesTab,
  SubscriptionsTab, AnalyticsTab, RecommendationsTab,
  SettingsTab, PromoCodesTab, FiltersTab, SeriesTab, GamesTab, BannersTab, StripeSyncTab, AvatarsTab, AdsTab, LoadingSpinner
} from '../components/admin';
import { NotificationsTab } from '../components/admin/NotificationsTab';
import { MailBotTab } from '../components/admin/MailBotTab';
import { SubscribersTab } from '../components/admin/SubscribersTab';
import { AppsTab } from '../components/admin/AppsTab';
import { TrashTab } from '../components/admin/TrashTab';
import { ReportsTab } from '../components/admin/ReportsTab';
import { TrailerStudioTab } from '../components/admin/TrailerStudioTab';
import { NewsletterStudio } from '../components/admin/newsletter/NewsletterStudio';
import { TeamTab } from '../components/admin/TeamTab';
import { SupportTab } from '../components/admin/SupportTab';
import { EmailsTab } from '../components/admin/EmailsTab';
import { StoreReviewsTab } from '../components/admin/StoreReviewsTab';
import { ProfileMenu } from '../components/admin/ProfileMenu';
import { NotifyCenter } from '../components/admin/NotifyCenter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'contents', label: 'Contenus', icon: Film },
  { id: 'series', label: 'Séries', icon: Tv },
  { id: 'fictions', label: 'Fictions', icon: Clapperboard },
  { id: 'documentaires', label: 'Documentaires', icon: Video },
  { id: 'jeunesse', label: 'Jeunesse', icon: Baby },
  { id: 'games', label: 'Jeux', icon: Joystick },
  { id: 'trash', label: 'Corbeille', icon: Trash2 },
  { id: 'reports', label: 'Signalements', icon: Flag },
  { id: 'trailer-studio', label: 'Bandes annonces', icon: Clapperboard },
  { id: 'newsletter', label: 'Newsletter', icon: Send },
  { id: 'banners', label: 'Bannières', icon: Image },
  { id: 'avatars', label: 'Avatars', icon: Smile },
  { id: 'categories', label: 'Catégories', icon: Tag },
  { id: 'subscribers', label: 'Abonnés', icon: UserCheck },
  { id: 'subscriptions', label: 'Abonnements', icon: CreditCard },
  { id: 'stripe-sync', label: 'Stripe Sync', icon: RefreshCw },
  { id: 'promo-codes', label: 'Codes Promos', icon: Ticket },
  { id: 'filters', label: 'Filtres', icon: Filter },
  { id: 'ads', label: 'Publicités', icon: Megaphone },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'recommendations', label: 'Recommandations', icon: Star },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'mailbot', label: 'Support IA', icon: Bot },
  { id: 'apps', label: 'Apps', icon: Smartphone },
  { id: 'support', label: 'Support', icon: Mail },
  { id: 'emails', label: 'Emails', icon: Send },
  { id: 'store_reviews', label: 'Avis Stores', icon: Star },
  { id: 'team', label: 'Utilisateurs', icon: Shield },
  { id: 'settings', label: 'Paramètres', icon: Settings },
];

/* ─── Theme helpers ─── */
const T = (theme) => theme === 'light';

function AdminAccessDenied() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const switchAccount = async () => {
    await logout();
    navigate('/auth', { state: { from: '/admin' } });
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }} data-testid="admin-access-denied">
      <div className="bg-[#1a0e2e]/80 border border-white/10 rounded-xl p-8 max-w-md w-full text-center">
        <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
          <Shield size={22} className="text-red-400" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">Accès réservé aux administrateurs</h1>
        <p className="text-sm text-white/50 mb-6">Le compte actuellement connecté n'a pas accès au dashboard. Connectez-vous avec un compte administrateur pour continuer.</p>
        <button onClick={switchAccount} data-testid="admin-switch-account-btn"
          className="w-full bg-[#E9641A] hover:bg-[#FE975E] text-white font-bold text-sm py-2.5 rounded-lg transition-colors mb-2">
          Se connecter avec un compte admin
        </button>
        <button onClick={() => navigate('/')} data-testid="admin-back-home-btn"
          className="w-full text-white/50 hover:text-white text-sm py-2 transition-colors">
          Retour à Maya+
        </button>
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [collapsed, setCollapsed] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [viewsData, setViewsData] = useState([]);
  const [statsDays, setStatsDays] = useState(0);
  const [theme, setTheme] = useState(() => localStorage.getItem('admin_theme') || 'dark');
  const [newReports, setNewReports] = useState(0);
  const [mobileNav, setMobileNav] = useState(false);
  const [showNotify, setShowNotify] = useState(false);

  // PWA admin : manifest + icône iOS quand on est sur /admin
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prevHref = link ? link.getAttribute('href') : null;
    if (link) link.setAttribute('href', '/manifest-admin.json');
    let apple = document.querySelector('link[rel="apple-touch-icon"]');
    if (!apple) { apple = document.createElement('link'); apple.rel = 'apple-touch-icon'; document.head.appendChild(apple); }
    const prevApple = apple.getAttribute('href');
    apple.setAttribute('href', '/admin-icon-180.png');
    let meta = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'apple-mobile-web-app-capable'; meta.content = 'yes'; document.head.appendChild(meta); }
    return () => {
      if (link && prevHref) link.setAttribute('href', prevHref);
      if (prevApple) apple.setAttribute('href', prevApple);
    };
  }, []);
  const [tabOrder, setTabOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_tab_order')) || []; } catch { return []; }
  });
  const dragIdRef = React.useRef(null);
  const [dragging, setDragging] = useState(null);

  const isSuper = !!user?.is_superadmin;
  const canSee = React.useCallback((id) => {
    if (id === 'team') return isSuper;
    if (isSuper || !user?.allowed_tabs || user.allowed_tabs.length === 0) return true;
    return user.allowed_tabs.includes(id);
  }, [isSuper, user]);

  const orderedTabs = React.useMemo(() => {
    const known = tabOrder.map(id => TABS.find(t => t.id === id)).filter(Boolean);
    const rest = TABS.filter(t => !tabOrder.includes(t.id));
    return [...known, ...rest].filter(t => canSee(t.id));
  }, [tabOrder, canSee]);

  useEffect(() => {
    if (user && !canSee(tab)) {
      const first = TABS.find(t => canSee(t.id));
      if (first) setTab(first.id);
    }
  }, [user, tab, canSee]);

  const handleDragEnter = (overId) => {
    const dragId = dragIdRef.current;
    if (!dragId || dragId === overId) return;
    const ids = orderedTabs.map(t => t.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(overId);
    ids.splice(from, 1);
    ids.splice(to, 0, dragId);
    setTabOrder(ids);
  };

  const handleDragEnd = () => {
    if (dragIdRef.current) localStorage.setItem('admin_tab_order', JSON.stringify(orderedTabs.map(t => t.id)));
    dragIdRef.current = null;
    setDragging(null);
  };

  useEffect(() => {
    const loadCount = () => axios.get(`${API}/admin/reports/unread-count`, { withCredentials: true })
      .then(r => setNewReports(r.data.count || 0)).catch(() => {});
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [tab]);

  const light = T(theme);
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('admin_theme', next);
  };

  const [openSupport, setOpenSupport] = useState(0);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const fetchCount = () => axios.get(`${API}/admin/support/open-count`, { withCredentials: true })
      .then(r => setOpenSupport(r.data.count || 0)).catch(() => {});
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => clearInterval(iv);
  }, [user, tab]);

  useEffect(() => {
    if (!user || !['admin', 'editor', 'analyst'].includes(user.role)) return;
    axios.get(`${API}/admin/analytics`, { params: statsDays ? { days: statsDays } : {}, withCredentials: true }).then(r => setAnalytics(r.data)).catch(() => {});
    axios.get(`${API}/admin/analytics/views`, { params: { days: statsDays > 0 ? statsDays : 30 }, withCredentials: true }).then(r => setViewsData(r.data)).catch(() => {});
  }, [user, navigate, tab, statsDays]);

  if (user && !['admin', 'editor', 'analyst'].includes(user.role)) {
    return <AdminAccessDenied />;
  }

  const tabCounts = analytics ? {
    contents: analytics.total_contents,
    series: analytics.type_stats?.series || 0,
    fictions: analytics.type_stats?.fiction || 0,
    documentaires: analytics.type_stats?.documentaire || 0,
    jeunesse: analytics.type_stats?.jeunesse || 0,
    games: analytics.type_stats?.game || 0,
  } : {};

  const renderTab = () => {
    switch (tab) {
      case 'dashboard': return <DashboardTab analytics={analytics} viewsData={viewsData} theme={theme} statsDays={statsDays} setStatsDays={setStatsDays} />;
      case 'contents': return <ContentsTab key="contents" theme={theme} />;
      case 'series': return <SeriesTab theme={theme} />;
      case 'fictions': return <ContentsTab key="fictions" filterType="fiction" theme={theme} />;
      case 'documentaires': return <ContentsTab key="documentaires" filterType="documentaire" theme={theme} />;
      case 'jeunesse': return <ContentsTab key="jeunesse" filterType="jeunesse" theme={theme} />;
      case 'games': return <GamesTab theme={theme} />;
      case 'trash': return <TrashTab theme={theme} />;
      case 'reports': return <ReportsTab theme={theme} />;
      case 'trailer-studio': return <TrailerStudioTab theme={theme} />;
      case 'newsletter': return <NewsletterStudio theme={theme} />;
      case 'banners': return <BannersTab theme={theme} />;
      case 'avatars': return <AvatarsTab theme={theme} />;
      case 'categories': return <CategoriesTab theme={theme} />;
      case 'subscribers': return <SubscribersTab theme={theme} />;
      case 'subscriptions': return <SubscriptionsTab theme={theme} />;
      case 'stripe-sync': return <StripeSyncTab theme={theme} />;
      case 'promo-codes': return <PromoCodesTab theme={theme} />;
      case 'filters': return <FiltersTab theme={theme} />;
      case 'ads': return <AdsTab theme={theme} />;
      case 'analytics': return <AnalyticsTab analytics={analytics} viewsData={viewsData} theme={theme} statsDays={statsDays} setStatsDays={setStatsDays} />;
      case 'recommendations': return <RecommendationsTab theme={theme} />;
      case 'notifications': return <NotificationsTab theme={theme} />;
      case 'mailbot': return <MailBotTab theme={theme} />;
      case 'apps': return <AppsTab theme={theme} />;
      case 'support': return <SupportTab theme={theme} />;
      case 'emails': return <EmailsTab theme={theme} />;
      case 'store_reviews': return <StoreReviewsTab theme={theme} />;
      case 'team': return <TeamTab theme={theme} tabs={TABS.filter(t => t.id !== 'team')} />;
      case 'settings': return <SettingsTab theme={theme} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: light ? '#f1f5f9' : 'linear-gradient(180deg, #11041F 0%, #1a0e2e 100%)' }} data-testid="admin-dashboard">
      {/* Backdrop mobile */}
      {mobileNav && <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setMobileNav(false)} data-testid="admin-mobile-backdrop" />}
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 bottom-0 z-40 flex flex-col transition-all duration-300 w-64 ${mobileNav ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 ${collapsed ? 'md:w-14' : 'md:w-52'} ${light ? 'bg-white border-r border-gray-200' : 'bg-[#0d0520] border-r border-white/5'}`} data-testid="admin-sidebar">
        <div className={`flex items-center gap-2 px-3 h-14 border-b ${light ? 'border-gray-200' : 'border-white/5'}`}>
          <img src={LOGO_URL} alt="Maya+" className={`h-6 ${collapsed ? 'md:hidden' : ''}`} />
          <span className={`text-[10px] text-[#E9641A] font-bold tracking-wider ${collapsed ? 'md:hidden' : ''}`}>ADMIN</span>
          <button onClick={() => setCollapsed(!collapsed)} className={`ml-auto transition-colors hidden md:block ${light ? 'text-gray-400 hover:text-gray-700' : 'text-white/30 hover:text-white'}`}>
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
          <button onClick={() => setMobileNav(false)} className={`ml-auto md:hidden ${light ? 'text-gray-400' : 'text-white/40'}`} data-testid="admin-mobile-close">
            <ChevronLeft size={16} />
          </button>
        </div>
        <nav className="flex-1 py-3 space-y-0.5 px-1.5 overflow-y-auto">
          {orderedTabs.map(t => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => { setTab(t.id); setMobileNav(false); }}
              draggable
              onDragStart={(e) => { dragIdRef.current = t.id; setDragging(t.id); e.dataTransfer.effectAllowed = 'move'; }}
              onDragEnter={() => handleDragEnter(t.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
              onDragEnd={handleDragEnd}
              className={`relative w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all ${dragging === t.id ? 'opacity-40 ring-1 ring-dashed ring-[#E9641A]' : ''} ${
                tab === t.id
                  ? 'bg-[#E9641A]/10 text-[#E9641A]'
                  : light
                    ? 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                    : 'text-white/40 hover:text-white hover:bg-white/5'
              }`}>
              <t.icon size={16} className="flex-none" />
              <span className={collapsed ? 'md:hidden' : ''}>{t.label}{tabCounts[t.id] !== undefined ? ` (${tabCounts[t.id]})` : ''}</span>
              {t.id === 'reports' && newReports > 0 && (
                <span data-testid="reports-badge" className={`${collapsed ? 'absolute top-0.5 right-0.5' : 'ml-auto'} min-w-[16px] h-[16px] px-1 rounded-full bg-[#E9641A] text-white text-[9px] font-bold flex items-center justify-center leading-none`}>
                  {newReports > 9 ? '9+' : newReports}
                </span>
              )}
              {t.id === 'support' && openSupport > 0 && (
                <span data-testid="support-badge" className={`${collapsed ? 'absolute top-0.5 right-0.5' : 'ml-auto'} min-w-[16px] h-[16px] px-1 rounded-full bg-[#E9641A] text-white text-[9px] font-bold flex items-center justify-center leading-none`}>
                  {openSupport > 9 ? '9+' : openSupport}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className={`px-1.5 pb-2 pt-1 border-t ${light ? 'border-gray-200' : 'border-white/5'}`}>
          {/* Theme toggle */}
          <button onClick={toggleTheme} data-testid="admin-theme-toggle"
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all mb-1 ${
              light ? 'text-gray-500 hover:text-gray-800 hover:bg-gray-100' : 'text-white/40 hover:text-white hover:bg-white/5'
            }`}>
            {light ? <Moon size={16} className="flex-none" /> : <Sun size={16} className="flex-none" />}
            <span className={collapsed ? 'md:hidden' : ''}>{light ? 'Mode sombre' : 'Mode clair'}</span>
          </button>
          <button onClick={() => navigate('/')} className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs transition-all ${light ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-100' : 'text-white/25 hover:text-white hover:bg-white/5'}`} data-testid="admin-go-front">
            <ArrowLeft size={16} className="flex-none" />
            <span className={collapsed ? 'md:hidden' : ''}>Retour au site</span>
          </button>
        </div>
      </aside>

      {/* Header */}
      <div className={`fixed top-0 right-0 z-30 h-14 backdrop-blur-xl flex items-center justify-between px-3 md:px-5 transition-all left-0 ${collapsed ? 'md:left-14' : 'md:left-52'} ${light ? 'bg-white/80 border-b border-gray-200' : 'bg-[#11041F]/90 border-b border-white/5'}`}>
        <div className="flex items-center gap-2">
          <button onClick={() => setMobileNav(true)} className={`md:hidden p-1.5 rounded-lg ${light ? 'text-gray-600 hover:bg-gray-100' : 'text-white/70 hover:bg-white/10'}`} data-testid="admin-mobile-menu-btn">
            <Menu size={18} />
          </button>
          <h2 className={`text-xs font-semibold capitalize ${light ? 'text-gray-500' : 'text-white/60'}`}>{TABS.find(t => t.id === tab)?.label}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowNotify(true)} className={`p-1.5 rounded-lg transition-colors ${light ? 'text-gray-500 hover:bg-gray-100' : 'text-white/50 hover:bg-white/10'}`} data-testid="admin-notify-btn" title="Notifications mobiles">
            <Bell size={16} />
          </button>
          <ProfileMenu theme={theme} />
        </div>
      </div>

      {/* Content */}
      <main className={`pt-20 pb-10 px-3 md:px-5 transition-all ml-0 overflow-x-auto ${collapsed ? 'md:ml-14' : 'md:ml-52'}`}>
        {renderTab()}
      </main>
      {showNotify && <NotifyCenter theme={theme} onClose={() => setShowNotify(false)} />}
    </div>
  );
}
